/**
 * Tipos de estado de la aplicación Exocortex
 */

import type { Frequency, Layer, Protocol } from './protocol.types';

/**
 * Progreso del día
 * Representa el estado de completado para una fecha específica
 */
export interface DayProgress {
  /** Fecha en formato YYYY-MM-DD */
  date: string;

  /** Si la capa de supervivencia está completa */
  survivalComplete: boolean;

  /** Si la capa de mantenimiento está completa */
  maintenanceComplete: boolean;

  /** Si la capa de crecimiento está completa */
  growthComplete: boolean;

  /** Total de tareas para el día */
  totalTasks: number;

  /** Tareas completadas */
  completedTasks: number;

  /** Tiempo gastado en minutos */
  timeSpent: number;

  /** Tiempo estimado total en minutos */
  timeEstimated: number;

  /** Porcentaje de completado (0-100) */
  percentage: number;
}

/**
 * Progreso por capa
 */
export interface LayerProgress {
  layer: Layer;
  total: number;
  completed: number;
  percentage: number;
  isUnlocked: boolean;
  isComplete: boolean;
}

/**
 * Preferencias del usuario
 */
export interface UserPreferences {
  // === Apariencia ===

  /** Tema de la aplicación */
  theme: 'dark' | 'light' | 'system';

  /** Si usar modo compacto */
  compactMode: boolean;

  /** Mostrar fundamentos científicos por defecto */
  showRationale: boolean;

  /** Vista inicial al abrir la app */
  defaultView: Frequency;

  // === Notificaciones ===

  /** Si las notificaciones están habilitadas */
  notificationsEnabled: boolean;

  /** Si los sonidos están habilitados */
  soundEnabled: boolean;

  /** Si la vibración está habilitada (móvil) */
  hapticEnabled: boolean;

  // === Horarios ===

  /** Hora de despertar (HH:mm) */
  wakeTime: string;

  /** Hora de dormir (HH:mm) */
  sleepTime: string;

  /** Zona horaria */
  timezone: string;

  // === Comportamiento ===

  /** Auto-reset de tareas diarias */
  autoResetDaily: boolean;

  /** Hora del auto-reset (HH:mm) */
  autoResetTime: string;

  /** Mostrar animaciones */
  animationsEnabled: boolean;

  /** Reducir movimiento (accesibilidad) */
  reduceMotion: boolean;

  /** Usar colores suaves/desaturados (sensibilidad visual) */
  softColors: boolean;

  /** Modo alto contraste (WCAG AAA) */
  highContrast: boolean;

  /** Fuente accesible (dislexia) */
  fontFamily: 'default' | 'dyslexic' | 'mono';
}

/**
 * Estadísticas de uso
 */
export interface UsageStats {
  /** Total de protocolos completados históricamente */
  totalCompletions: number;

  /** Mejor racha histórica */
  bestStreak: number;

  /** Racha actual */
  currentStreak: number;

  /** Días usando la app */
  daysActive: number;

  /** Tiempo total invertido (minutos) */
  totalTimeSpent: number;

  /** Promedio de completado diario (%) */
  averageDailyCompletion: number;

  /** Capa más completada */
  strongestLayer: Layer;

  /** Capa que necesita más atención */
  weakestLayer: Layer;
}

/**
 * Registro histórico de un día
 */
export interface DayRecord {
  /** Fecha en formato YYYY-MM-DD */
  date: string;

  /** IDs de protocolos completados */
  completedProtocols: string[];

  /** Progreso del día */
  progress: DayProgress;

  /** Notas del día (opcional) */
  notes?: string;

  /** Nivel de energía reportado (1-5) */
  energyLevel?: number;

  /** Estado de ánimo (1-5) */
  moodLevel?: number;
}

/**
 * Estado de sincronización
 */
export interface SyncState {
  /** Si está online */
  isOnline: boolean;

  /** Última sincronización exitosa */
  lastSync: string | null;

  /** Si hay cambios pendientes */
  hasPendingChanges: boolean;

  /** Número de cambios pendientes */
  pendingChangesCount: number;

  /** Si está sincronizando ahora */
  isSyncing: boolean;

  /** Error de sincronización (si existe) */
  syncError: string | null;
}

/**
 * Estado global de la aplicación
 */
export interface AppState {
  /** Lista de protocolos */
  protocols: Protocol[];

  /** Frecuencia activa en el dashboard */
  activeFrequency: Frequency;

  /** Fecha seleccionada */
  selectedDate: string;

  /** Progreso del día actual */
  dayProgress: DayProgress;

  /** Preferencias del usuario */
  preferences: UserPreferences;

  /** Estado de sincronización */
  sync: SyncState;

  /** Si la app está inicializada */
  isInitialized: boolean;

  /** Si está cargando datos */
  isLoading: boolean;
}

/**
 * Valores por defecto para preferencias
 */
export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'dark',
  compactMode: false,
  showRationale: true,
  defaultView: 'daily',
  notificationsEnabled: true,
  soundEnabled: true,
  hapticEnabled: true,
  wakeTime: '07:00',
  sleepTime: '23:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  autoResetDaily: true,
  autoResetTime: '04:00',
  animationsEnabled: true,
  reduceMotion: false,
  softColors: false,
  highContrast: false,
  fontFamily: 'default',
};
